import { Fragment } from "react"

// ** Third Party Components
import { z } from "zod"
import { useForm } from "react-hook-form"
import { useLocation, useParams } from "react-router-dom"
import { zodResolver } from "@hookform/resolvers/zod"
import { CloseCircle, TickCircle } from "iconsax-reactjs"
import { useMutation, useQueryClient } from "@tanstack/react-query"

// ** Theme Components
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../common/form"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Types
import { DictType } from "@pma/types"

// ** Services
import { createNewFolderService } from "@pma/services/files"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

const folderSchema = z.object({
  name: z.string().trim().min(1, "Folder name is required"),
  parent_id: z.number().optional(),
  organization_id: z.number().optional(),
})

type FolderFormValues = z.infer<typeof folderSchema>

interface NewFolderProps {
  onClose: () => void
  isMobile?: boolean
}

const NewFolder = ({ onClose, isMobile = false }: NewFolderProps) => {
  const { folderId } = useParams()
  const location = useLocation()
  const { authConfig } = useAuth()
  const queryClient = useQueryClient()
  const { translate } = useTranslator()

  const organization_id = authConfig.last_organization_id
  const form = useForm<FolderFormValues>({
    resolver: zodResolver(folderSchema),
    defaultValues: { name: "", parent_id: Number(folderId) || 0, organization_id: organization_id },
  })

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = form

  const onSubmit = (data: FolderFormValues) => createNewFolderMutation.mutate(data)

  const createNewFolderMutation = useMutation({
    mutationKey: ["create-new-folder"],
    mutationFn: createNewFolderService,
    onSuccess: async (data) => {
      onClose()
      toast({
        title: translate("Success"),
        description: `${data?.name} ${translate("folder created successfully")}`,
        variant: "success",
      })
      if (data.project_id === 0)
        await queryClient.invalidateQueries({ queryKey: ["get-parent-folder-list-view"] })
      await queryClient.invalidateQueries({ queryKey: ["get-sub-folders-and-files"] })
      if (location.pathname.includes("/files")) {
        await queryClient.invalidateQueries({ queryKey: ["get-parent-and-sub-folder-with-filter"] })
      }
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data?.message || translate("Unable to create folder"),
        variant: "error",
      })
    },
  })

  return (
    <Fragment>
      {!isMobile ? (
        <div className='flex gap-4 border-b border-neutral-200 px-6 py-3'>
          <div className='flex w-full items-center justify-between'>
            <h2 className='font-[inter] text-lg font-medium text-neutral-800'>
              {translate("New Folder")}
            </h2>
            <CloseCircle className='cursor-pointer text-neutral-800' size={24} onClick={onClose} />
          </div>
        </div>
      ) : null}

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            className={cn("flex flex-col gap-3 overflow-auto border-b border-neutral-200", isMobile ? "p-3" : "px-6 py-5")}
            style={isMobile ? {} : { maxHeight: "calc(100vh - 320px)" }}
          >
            <FormField
              control={control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='!text-xs !font-normal'>
                    {translate("Folder Name")} <span className='text-red-500'>*</span>
                  </FormLabel>
                  <FormControl>
                    <input
                      {...field}
                      className={cn(
                        "w-full rounded-xl border px-4 py-3 text-sm outline-none !mt-0",
                        errors?.name
                          ? "border-error-300 focus:border-error-500 focus:ring-2 focus:ring-error-500"
                          : "border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                      )}
                      placeholder={translate("Enter folder name")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={cn("flex items-center justify-end gap-3", isMobile ? "p-3" : "px-6 py-4")}>
            <button
              className='rounded-full border border-neutral-200 px-6 py-2.5 font-[inter] text-sm font-normal text-neutral-800'
              type='button'
              onClick={onClose}
            >
              {translate("Cancel")}
            </button>
            <button
              className='flex rounded-full bg-indigo-600 px-6 py-2.5 font-[inter] text-sm font-medium text-white'
              type='submit'
              disabled={createNewFolderMutation.isPending}
              style={
                watch("name")
                  ? {
                    background:
                      "linear-gradient(0deg, var(--Indigo-8, #444CE7), var(--Indigo-8, #444CE7)), linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
                    boxShadow: "0px 0px 0px 1.8px #FFFFFF40 inset",
                  }
                  : {
                    background:
                      "linear-gradient(0deg, var(--Grey-5, #94A3B8), var(--Grey-5, #94A3B8)),linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 100%)",
                    boxShadow: "0px 0px 0px 1.8px #FFFFFF40 inset",
                  }
              }
            >
              {createNewFolderMutation.isPending ? (
                <TickCircle size='16' color='#FFFFFF' />
              ) : (
                translate("Save")
              )}{" "}
            </button>
          </div>
        </form>
      </Form>
    </Fragment>
  )
}

export default NewFolder
